import { SelectionModel } from '@angular/cdk/collections';
import { NgIf, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { TableDesktopComponent } from './components/table-desktop/table-desktop.component';
import { TableMobileComponent } from './components/table-mobile/table-mobile.component';
import {
  TableConfig,
  TableColumnTypes,
  TableColumnTypesFixed,
  TableStickyTypes,
  TableColumnStickyTypes,
  TableColumnAction,
} from './table.model';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  imports: [NgIf, NgTemplateOutlet, MatPaginator, NotificationEmptyComponent, AsyncPipe],
})
export class TableComponent implements OnDestroy {
  private resolver = inject(ComponentFactoryResolver);
  private container = inject(ViewContainerRef);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);

  @Input() emptyTitle: string;
  @Input() emptyDescription: string;
  @Input() emptyImage: string;
  @Input() emptyActionIcon: string;
  @Input() emptyActionText: string;
  @Input() emptyActionColor: ThemePalette;
  @Input() showEmptyAction = false;
  @Input() isEmpty: boolean;
  @Output() emptyAction = new EventEmitter();
  @Output() paginatorChange = new EventEmitter();

  public columnIds: string[] = [];
  public cardView$ = this.breakpoint.ltMd$.pipe(map((ltMd) => ltMd));
  public singleAction: TableColumnAction | undefined;
  public multipleActions: TableColumnAction[] = [];
  private dataSourceSubscription: Subscription;

  _config: TableConfig = {
    dataSource: new MatTableDataSource([]),
    columns: [],
    hiddenColumns: [],
    header: true,
    selectColumn: true,
    visibleCardColumns: 3,
    pagination: true,
    actions: [],
  };

  @Input()
  set config(config: TableConfig) {
    const columns = (config.columns ?? []).filter(this.isDefined);
    const actions = (config.actions ?? []).filter(this.isDefined);

    if (!config.pagination || typeof config.pagination === 'boolean') {
      config.dataSource.paginator = this.paginatorElement;
    } else if (config.pagination) {
      config.dataSource.paginator = config.pagination;
    }
    this._config = {
      ...this._config,
      ...config,
      columns,
      actions,
    };

    this.columnIds = [
      ...(this.config.dragDrop ? [TableColumnTypesFixed.DRAG_DROP] : []),
      ...(this.config.selectColumn ? [TableColumnTypesFixed.SELECT] : []),
      ...this.config.columns.map((c) => c.id),
      ...(!!this.config.actionColumn || !!actions.length ? [TableColumnTypesFixed.ACTION] : []),
    ];

    this.singleAction = actions.length === 1 ? actions[0] : actions.find(({ primary }) => primary);
    this.multipleActions = actions.filter((action) => action !== this.singleAction);

    if (this.dataSourceSubscription) {
      this.dataSourceSubscription.unsubscribe();
    }

    this.dataSourceSubscription = this._config.dataSource.connect().subscribe((_) => {
      columns
        .filter(
          ({ type }) =>
            type === TableColumnTypes.INPUT_STRING || type === TableColumnTypes.INPUT_NUMBER,
        )
        .forEach(({ id, inputConfig }) => {
          if (inputConfig) {
            inputConfig.formArray.clear();
            config.dataSource.data.forEach((item) => {
              const formControl = new FormControl(
                (item as { [key: string]: unknown })[id],
                inputConfig.options,
              );
              inputConfig.formArray.push(formControl);
            });
          }
        });
    });
  }

  get config() {
    return this._config;
  }

  public selection: SelectionModel<unknown> = new SelectionModel<unknown>(true, [], true);
  @Output() selectionChange = this.selection.changed;

  public allRowsSelected$ = this.selection.changed.pipe(
    map(
      () =>
        this.selection.hasValue() &&
        this.selection.selected.length === this.config.dataSource.data.length,
    ),
  );

  public isIndeterminate$ = this.selection.changed.pipe(
    map(
      () =>
        this.selection.hasValue() &&
        this.selection.selected.length !== this.config.dataSource.data.length,
    ),
  );

  public readonly selectAllCheckbox = new FormControl();
  public readonly tableColumnTypes = TableColumnTypes;
  public readonly tableColumnTypesFixed = TableColumnTypesFixed;
  public readonly tableStickyTypes = TableStickyTypes;
  public readonly tableColumnStickyTypes = TableColumnStickyTypes;
  public view: TemplateRef<unknown>;
  public mobileComponentRef: ComponentRef<TableMobileComponent>;
  public desktopComponentRef: ComponentRef<TableDesktopComponent>;
  public view$ = this.breakpoint.ltMd$.pipe(
    map((ltMd) =>
      ltMd && !this.config.disableCards
        ? this.mobileComponentRef.instance.template
        : this.desktopComponentRef.instance.template,
    ),
  );

  public selectionSubscription = this.selectAllCheckbox.valueChanges.subscribe(
    (selected: boolean) => {
      this.toggleAll(selected);
    },
  );

  @ViewChild('paginatorElement') paginatorElement: MatPaginator;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    if (!this.config.disableCards) {
      this.mobileComponentRef = this.container.createComponent(
        this.resolver.resolveComponentFactory(TableMobileComponent),
      );
    }
    this.desktopComponentRef = this.container.createComponent(
      this.resolver.resolveComponentFactory(TableDesktopComponent),
    );
  }

  ngOnDestroy() {
    if (this.dataSourceSubscription) {
      this.dataSourceSubscription.unsubscribe();
    }
  }

  private isDefined<T>(value: T | null | undefined): value is T {
    return value !== undefined && value !== null;
  }

  private toggleAll(selected: boolean): void {
    if (selected) {
      this.selection.select(...this.config.dataSource.data);
    } else {
      this.selection.clear();
    }
  }
}
